import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  Box,
  OutlinedInput,
  Chip,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useMaterialUIController } from "context";
import { useTranslation } from "react-i18next";

const MAX_VISIBLE_JOBS = 5; // Adjust this number based on your preference

const fieldStyles = {
  height: 60, // Adjust overall height
  "& .MuiInputBase-root": {
    height: 60, // Input field height
    display: "flex",
    alignItems: "center",
  },
  "& .MuiSelect-select": {
    padding: 12, // Adjust padding inside select
  },
};

const BranchFormDialog = ({
  open,
  onClose,
  onSubmit,
  branch,
  selectedCompany,
  jobs,
  editMode,
  handleDelete,
  jobToBranch,
  areas,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [nameError, setNameError] = useState(false);
  const [companyId, setCompanyId] = useState(selectedCompany);
  const [selectedJobs, setSelectedJobs] = useState([]);
  // const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);

  const [loading, setLoading] = useState(false);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    setName(branch?.branchName || "");
    setAddress(branch?.address || "");
    setCompanyId(selectedCompany);
    setSelectedJobs(branch?.jobs || []); // Load selected jobs if in edit mode
    setNameError(false);
    // setSelectedArea(branch?.areaId || null);
    // If editing, filter jobs that belong to this branch
    if (editMode && branch) {
      // The job/branch links may not have loaded (or their request failed): start with none rather than crash.
      const assignedJobs = (jobToBranch || []).filter((item) => item.branchId === branch.branchId).map((item) => item.jobId);
      setSelectedJobs(assignedJobs);
    } else {
      setSelectedJobs([]); // Clear selection if adding new branch
      // setSelectedArea(null);
    }
  }, [branch, selectedCompany, open, jobToBranch, editMode]);

  useEffect(() => {
    if (open && branch) {
      setSelectedArea(branch.areaId || null);
    }
  }, [branch, open]);

  const handleNameChange = (e) => {
    setName(e.target.value);
    if (nameError) setNameError(false);
  };

  const handleAddressChange = (e) => {
    setAddress(e.target.value);
  };

  const handleCompanyChange = (e) => {
    setCompanyId(e.target.value);
  };

  const handleJobsChange = (event) => {
    setSelectedJobs(event.target.value);
  };

  const handleSubmit = async () => {
    if (!name) {
      setNameError(true);
      return;
    }
    setLoading(true);
    const updatedBranch = {
      ...branch,
      branchName: name,
      address: address,
      companyId: companyId,
      areaId: selectedArea,
    };
    onSubmit(updatedBranch, selectedJobs).then((e) => {
      setLoading(false);
      onClose();
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? "#333" : "#fff",
            color: darkMode ? "#fff" : "#000",
            borderRadius: 2,
            padding: 2,
          },
        }}
      >
        <DialogTitle>
          {editMode
            ? t("admin.branches.dialog.editBranch")
            : t("admin.branches.dialog.addNewBranch")}
        </DialogTitle>

        <DialogContent>
          <Autocomplete
            options={areas || []}
            getOptionLabel={(area) => area.areaName}
            value={(areas || []).find((area) => area.areaId === selectedArea) || null} // single area object
            onChange={(event, newValue) => {
              const newAreaId = newValue ? newValue.areaId : null;
              setSelectedArea(newAreaId);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.branches.dialog.selectArea")}
                margin="normal"
                fullWidth
              />
            )}
          />
          <TextField
            name="name"
            label={t("admin.branches.dialog.branchName") + " *"}
            value={name}
            onChange={handleNameChange}
            error={nameError}
            helperText={nameError ? t("admin.branches.dialog.branchNameError") : ""}
            fullWidth
            margin="normal"
          />

          <TextField
            name="address"
            label={t("admin.branches.dialog.branchAddress")}
            value={address}
            onChange={handleAddressChange}
            fullWidth
            margin="normal"
          />

          {/* Multi-Select Jobs Dropdown */}

          <Autocomplete
            multiple
            options={jobs}
            getOptionLabel={(job) => job.jobName}
            value={jobs?.filter((job) => selectedJobs.includes(job.jobId))} // Match selected jobs
            onChange={(event, newValue) => {
              setSelectedJobs(newValue.map((job) => job.jobId));
            }}
            renderTags={(selected, getTagProps) => {
              if (!jobs) return;
              const visibleJobs = selected.slice(0, MAX_VISIBLE_JOBS);
              const hiddenJobs = selected.length - MAX_VISIBLE_JOBS;

              return (
                <>
                  {visibleJobs.map((job, index) => (
                    <Chip key={job.jobId} label={job.jobName} {...getTagProps({ index })} />
                  ))}
                  {hiddenJobs > 0 && (
                    <Tooltip title={selected.map((job) => job.jobName).join(", ")}>
                      <Chip label={`+${hiddenJobs} more`} sx={{ cursor: "pointer" }} />
                    </Tooltip>
                  )}
                </>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.branches.dialog.selectJobs")}
                margin="normal"
                fullWidth
              />
            )}
          />
        </DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="flex-end" p={1}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <DialogActions>
            <Button onClick={onClose} color="secondary">
              {t("admin.branches.dialog.cancel")}
            </Button>
            {editMode && (
              <Button
                onClick={async () => {
                  setLoading(true);
                  await handleDelete(branch.branchId);
                  setLoading(false);
                  onClose();
                }}
                color="error"
                sx={{ color: "red", "&:hover": { backgroundColor: "red", color: "#fff" } }}
              >
                {t("admin.branches.dialog.delete")}
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              color="primary"
              sx={{ "&:hover": { backgroundColor: "blue", color: "#fff" } }}
            >
              {editMode ? t("admin.branches.dialog.update") : t("admin.branches.dialog.add")}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

BranchFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  branch: PropTypes.object.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  jobs: PropTypes.array.isRequired,
  editMode: PropTypes.bool.isRequired,
  handleDelete: PropTypes.func.isRequired,
  jobToBranch: PropTypes.array.isRequired,
  areas: PropTypes.array.isRequired,
};

export default BranchFormDialog;
